import {googleTagManagerBeginCheckoutEvent} from "@CheckOrder/functions/googleTagManager"
import useOnUnmount from "@Hooks/lifecycle/useOnUnmount"
import useHandleStripePayment from "@CheckOrder/hooks/useHandleStripePayment"
import useCheckOrderSubmissionEvent from "@CheckOrder/hooks/useCheckOrderSubmissionEvent"
import React from "react"
import {useAppSelector} from "@Hooks/selector/useAppSelector"
import {useAppDispatch} from "@Hooks/dispatch/useAppDispatch"
import {
    checkPasswordValidation,
    setCheckOrderUserMessages,
    setPassengerDataUpdating,
    setUserConfirmed,
    showCheckOrderModals
} from "@CheckOrder/reducers/checkOrderSlice"
import useUpdatePassengers from "@CheckOrder/hooks/useUpdatePassengers"
import loginAndReturnResponse from "@User/functions/loginAndReturnResponse"
import {setUser} from "@MyProfile/reducers/userProfileSlice"
import useSaveCheckOrderSession from "@CheckOrder/hooks/useSaveCheckOrderSession";
import {useLazyUpdatePassengerClassesQuery} from "@CheckOrder/api/checkOrderApi";
import preparePassengerClassesRequest from "@CheckOrder/api/functions/preparePassengerClassesRequest";
import {UpdatePassengerClassesResponseType} from "@CheckOrder/api/types/UpdatePassengerClassesQuery.type";
import {useIsAircash} from "@Hooks/selector/useIsAircash";
import useHandleAircashPayment from "@CheckOrder/hooks/useHandleAircashPayment";

interface UseConfirmAndPayAttributes {
    ipgFormRef: React.MutableRefObject<any>,
    privacyPolicyChecked: boolean,
    mustBePrintedPolicyChecked: boolean,
    newsletterAccepted: boolean,
}

export default function useConfirmAndPay(params: UseConfirmAndPayAttributes) {
    const {
        ipgFormRef,
        privacyPolicyChecked,
        mustBePrintedPolicyChecked,
        newsletterAccepted,
    } = params

    const {
        ticket,
        stripeEnabled,
        tripReviewAccepted,
        password,
        email,
        tokens,
        selectedServices: {
            isBuyingPremiumSupport,
            isBuyingSMSNotification,
            isBuyingTier1FlexibleCancellation,
        },
    } = useAppSelector((state) => state?.checkOrder)
    const isAircash = useIsAircash();
    const {user} = useAppSelector((state) => (state?.userProfile))
    const [updatePassengerClasses] = useLazyUpdatePassengerClassesQuery()
    const {handleStripePayment} = useHandleStripePayment({
        newsletterAccepted,
        tripReviewAccepted,
        privacyPolicyChecked,
    })
    const {handleAircashPayment} = useHandleAircashPayment({
        newsletterAccepted,
        tripReviewAccepted,
        privacyPolicyChecked,
    })

    const saveCheckOrderSession = useSaveCheckOrderSession()

    const {updateAllPassengersAndVerifySubmission, checkUserFieldValidation} = useUpdatePassengers()
    const {dispatchRedirectToPaymentEvent} = useCheckOrderSubmissionEvent({
        tripReviewAccepted,
        newsletterAccepted,
        privacyPolicyAccepted: privacyPolicyChecked,
    })

    const dispatch = useAppDispatch()

    useOnUnmount(() => {
        dispatch(setPassengerDataUpdating(false))
        dispatch(setUserConfirmed(false))
    })

    const tryToLoginUserIfPasswordFilled = async () => {
        if (user?.userLoginMethods?.status && (!user?.email && user?.password)) {
            // todo: revisit this IF with LLalic
            const loginResponse = await loginAndReturnResponse(
                email,
                password,
                ticket?.ticketCode,
                tokens?.csrfTokenBuyerInfo
            )

            if (loginResponse.status !== 200) {
                return false
            }

            if (loginResponse?.data?.data?.errors || loginResponse?.data?.error) {
                dispatch(setCheckOrderUserMessages([{
                    error: {
                        userMessage: Object.values(loginResponse?.data?.data?.errors ?? loginResponse?.data?.error)[0] as string,
                        okBtnText: "OK",
                        deliveryType: "alert",
                    },
                    additionalData: {},
                }]))

                return false
            }

            dispatch(setUser(loginResponse.data.data.user))
            dispatch(checkPasswordValidation(true))
        }

        return true
    }
    const isBuyingAnyAncillary = isBuyingPremiumSupport || isBuyingSMSNotification || isBuyingTier1FlexibleCancellation;

    return async () => {
        dispatch(setUserConfirmed(true))
        dispatch(showCheckOrderModals(true))
        const loginResponse = await tryToLoginUserIfPasswordFilled()
        if (loginResponse && checkUserFieldValidation() && privacyPolicyChecked && mustBePrintedPolicyChecked) {
            dispatch(setPassengerDataUpdating(true))

            updateAllPassengersAndVerifySubmission().then(async (submissionValid) => {
                if (submissionValid) {
                    let updatedCheckOrderData: UpdatePassengerClassesResponseType = null

                    if (ticket.singleSelectedPassengerClasses || ticket.returnSelectedPassengerClasses) {
                        const selectedSingleClasses = preparePassengerClassesRequest(ticket.singleSelectedPassengerClasses)
                        const selectedReturnClasses = preparePassengerClassesRequest(ticket.returnSelectedPassengerClasses)

                        if (selectedSingleClasses.length || selectedReturnClasses.length) {
                            updatedCheckOrderData = await updatePassengerClasses({
                                selectedSingleClasses,
                                selectedReturnClasses,
                                ticketCode: ticket.ticketCode,
                            }).unwrap()

                            if (!updatedCheckOrderData
                                || updatedCheckOrderData.checkOrder.ticket.singleSelectedPassengerClasses.noLongerAvailablePassengerClasses.length
                                || updatedCheckOrderData.checkOrder.ticket.returnSelectedPassengerClasses.noLongerAvailablePassengerClasses.length
                            ) {
                                dispatch(setPassengerDataUpdating(false))
                                return
                            }
                        }
                    }

                    saveCheckOrderSession()
                    if (ticket.voucher.ticketPriceFullyCoveredByVoucher && !isBuyingAnyAncillary) {
                        ipgFormRef.current.submit()
                    } else {
                        dispatchRedirectToPaymentEvent().then(() => {
                            if (isAircash) {
                                handleAircashPayment()
                            } else if (stripeEnabled) {
                                handleStripePayment(updatedCheckOrderData)
                            } else {
                                // by corvus, or by voucher => payment submitting with post request
                                ipgFormRef.current.submit()
                            }
                        })
                    }
                } else {
                    dispatch(setPassengerDataUpdating(false))
                }
            })
        }

        googleTagManagerBeginCheckoutEvent(ticket)
    }
}